import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'

import { useCoinContext } from '@/app/[locale]/price/[slug]/_contexts/CoinContext'
import { useExchangeRates } from '@/services/currency'
import { AllAvailableCurrencyCodes } from '@/types/currency'
import { fiatCurrencyFormat, formatPrice } from '@/utils/number/format'

export function useConverter(currency: AllAvailableCurrencyCodes) {
  const { coin } = useCoinContext()
  const { data: exchangeRates, isFetching: isRatesFetching } = useExchangeRates()
  const queryClient = useQueryClient()

  const [tokenText, setTokenText] = useState<string | number>('1.0')
  const [fiatText, setFiatText] = useState<string | number>('')
  const [isRefreshing, setIsRefreshing] = useState(false)

  const fiatRate = useMemo(() => {
    if (!exchangeRates) return 0
    const rates = exchangeRates
    const rate = rates[currency]
    return rate ? Number(rate) : 0
  }, [exchangeRates, currency])

  const oneTokenToFiatPrice = useMemo(() => {
    return (coin.usd_price || 0) * fiatRate
  }, [coin.usd_price, fiatRate])

  useEffect(() => {
    if (oneTokenToFiatPrice > 0) {
      const currentTokenVal = parseFloat(tokenText.toString())
      if (!isNaN(currentTokenVal)) {
        const convertedText = currentTokenVal * oneTokenToFiatPrice
        setFiatText(fiatCurrencyFormat(convertedText))
      }
    }
  }, [oneTokenToFiatPrice])

  const updateFiatText = (tokenValue: string) => {
    if (oneTokenToFiatPrice > 0) {
      const val = parseFloat(tokenValue)
      if (!isNaN(val)) {
        const convertedText = val * oneTokenToFiatPrice
        setFiatText(fiatCurrencyFormat(convertedText))
      } else {
        setFiatText('')
      }
    }
  }

  const handleTokenChange = (val: string | number) => {
    setTokenText(val)
    updateFiatText(val.toString())
  }

  const handleFiatChange = (val: string | number) => {
    const text = val.toString()
    if (oneTokenToFiatPrice > 0) {
      const convertToken = parseFloat(text) / oneTokenToFiatPrice
      if (!isNaN(convertToken)) {
        setTokenText(formatPrice(convertToken) || '')
      } else {
        setTokenText('')
      }
    }
    setFiatText(val)
  }

  const handleRefresh = async () => {
    setIsRefreshing(true)
    await queryClient.invalidateQueries({ queryKey: ['exchangeRates'] })
    setTimeout(() => setIsRefreshing(false), 500)
  }

  return {
    coin,
    tokenText,
    fiatText,
    oneTokenToFiatPrice,
    isRefreshing,
    isRatesFetching,
    handleTokenChange,
    handleFiatChange,
    handleRefresh,
  }
}
